import { useIsMutating } from "@tanstack/react-query"
import { GripVertical } from "lucide-react"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import isLocked from "@/modules/calendar/utils/is-locked"
import getFormattedTime from "@/modules/calendar/utils/get-formatted-time"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import getEventCategoryInfo from "@/modules/calendar/utils/get-event-category-info"

export default function EventComponent({
  event,
  isReadOnly = false,
}: {
  event: object
  isReadOnly?: boolean
}) {
  const calendarEvent = event as UserEvent
  const { startDate, endDate, parent } = calendarEvent
  const { color, category, title } = parent
  const isBlocked = isLocked(calendarEvent)
  const { openSideSheet } = useUserEventSideSheet()
  const { format } = useCalendarConfig()
  const time = `${getFormattedTime(startDate, format)} - ${getFormattedTime(endDate, format)}`
  const cardTitle = `${calendarEvent.parent.title} • ${time}`
  const isPending = useIsMutating({ mutationKey: ["update-event"] }) > 0

  const handleOnClickEvent = () => {
    openSideSheet({ type: "details", event: calendarEvent })
  }

  return (
    <div
      className={`flex h-full w-full flex-col justify-between overflow-hidden rounded border border-solid bg-white p-2.5 text-black shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${isBlocked ? "locked border-2" : "border-l-[6px]"} ${isPending ? "bg-gray-100 opacity-60" : ""} ${isBlocked ? "pointer-events-none" : ""} ${isReadOnly ? "pointer-events-none cursor-default" : "cursor-pointer"}`}
      style={{
        borderColor: isBlocked ? color : "",
        borderLeftColor: color,
      }}
      onClick={handleOnClickEvent}
      role="button"
      title={cardTitle}
    >
      <div className="flex w-full flex-col items-start gap-1 overflow-hidden">
        <p
          className={`flex w-full items-center justify-start gap-1 truncate text-sm font-medium`}
          style={{ color: isBlocked ? color : "rgb(76 76 76)" }}
        >
          <span>{getEventCategoryInfo(category).icon}</span>
          <span className="w-full truncate">{title}</span>
        </p>
        <p className="text-[11px] text-gray-500">{time}</p>
      </div>

      <div className="flex items-center justify-end overflow-hidden">
        {!isBlocked && <GripVertical className="!size-4 text-gray-400" />}
      </div>
    </div>
  )
}
